import { environment } from '@env/environment';
import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AlertsComponent } from '@gravity-angular/base';
import {
  ResponsiveService,
  SearchInputService,
  SearchOption,
  SideNavModel
} from '@gravity-angular/layout';
import { PreviousRouteService } from '@gravity-angular/utils';
import { Observable, of, lastValueFrom } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import packageInfo from '../../package.json';
import { ThemeService } from './core/services/theme.service';
import {
  AuthOptions,
  EventTypes,
  OidcSecurityService,
  PublicEventsService
} from 'angular-auth-oidc-client';
import { first } from 'rxjs/operators';
import { OidcStorageService } from './auth/oidc-config/oidc-storage.service';
import {
  OidcConfigIds,
  OidcCommonValues
} from './auth/models/user-management.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  sideNavContent: SideNavModel;
  appName: string;
  alerts: Portal<unknown>;
  showMainLayout: boolean;
  home: { location: string; text: string };
  userInitial: string;
  userName: string;
  userEmail: string;

  get isDark() {
    return this.themeService.isDark;
  }

  constructor(
    private readonly responsiveService: ResponsiveService,
    private readonly previousRouteService: PreviousRouteService,
    private readonly searchInputService: SearchInputService,
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly eventService: PublicEventsService,
    private readonly oidcStorageService: OidcStorageService
  ) {
    this.previousRouteService.initialize();
  }

  ngOnInit(): void {
    this.showMainLayout = true;
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.showMainLayout = true;
      }
    });

    this.appName = environment.appName;
    this.home = { location: '/', text: 'Home' };
    this.alerts = new ComponentPortal(AlertsComponent);
    // Restore from cache immediately (populated by OIDC event on first login)
    this.userName = localStorage.getItem('user') || '';
    this.userEmail = localStorage.getItem('email') || '';
    this.userInitial = this.userName ? this.userName.charAt(0).toUpperCase() : 'U';

    this.sideNavContent = {
      mode: 'side',
      opened: true,
      routes: [
        {
          route: 'live-data',
          display: 'Live Data',
          icon: 'show_chart',
          id: 'liveDataLink'
        },
        {
          route: 'historical-data',
          display: 'Historical Data',
          icon: 'history',
          id: 'historicalDataLink'
        },
        {
          route: 'alarms',
          display: 'Alarms',
          icon: 'notifications_active',
          id: 'alarmsLink'
        },
        {
          route: 'user/user-list',
          display: 'Users',
          icon: 'people',
          id: 'usersLink'
        },
        {
          route: 'role/role-list',
          display: 'Roles',
          icon: 'security',
          id: 'rolesLink'
        }
      ]
    };

    // Listen for user data from OIDC
    this.eventService
      .registerForEvents()
      .pipe(first(notification => notification.type === EventTypes.UserDataChanged))
      .subscribe(value => {
        const userData = value?.value?.userData;
        if (userData) {
          const givenName = userData.given_name || '';
          const familyName = userData.family_name || '';
          this.userName = `${givenName} ${familyName}`.trim();
          this.userEmail = userData.email || '';
          this.userInitial = givenName ? givenName.charAt(0).toUpperCase() : 'U';
          localStorage.setItem('user', this.userName);
          localStorage.setItem('email', this.userEmail);
        }
      });

    const searchConfig = {
      searchFn: (): Observable<SearchOption[]> => of(null),
      onOptionSelected: (event: MatAutocompleteSelectedEvent): void => {
        this.router.navigate([event.option.value.link]);
      }
    };
    this.searchInputService.initialize(searchConfig);
  }

  toggleSideNav(opened?: boolean): void {
    this.sideNavContent.opened = opened ?? !this.sideNavContent.opened;
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  async onLogout(): Promise<void> {
    sessionStorage.removeItem('route');
    localStorage.removeItem(OidcCommonValues.TAB_DETECTION);

    const customParams: AuthOptions = {
      urlHandler: url => {
        const newUrl = url.replace('id_token_hint=undefined', '');
        window.location.href = newUrl;
        localStorage.removeItem(OidcConfigIds.XGS);
      }
    };
    await lastValueFrom(
      this.oidcSecurityService.logoff(OidcConfigIds.XGS, customParams)
    );
  }

  openAboutDialog(): void {
    const modalData = {
      title: 'About',
      info: [
        `Name: ${this.appName}`,
        `Version: ${packageInfo.version}`,
        `Copyright © ${new Date().getFullYear()} Xylem, Inc`
      ]
    };
    console.log('About dialog:', modalData);
  }
}
